// Preview Studio Type Definitions

export type CreativeType = 'banner' | 'mraid' | 'video' | 'moments';
export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'ctv';
export type DeviceOS = 'ios' | 'android' | 'other';
export type PreviewMode = 'simulated' | 'live';
export type SourceMode = 'campaign' | 'raw_tag';

export interface MacroReplacement {
  macro: string;
  value: string;
}

export interface LocationInfo {
  name: string;
  lat: number;
  lon: number;
  address?: string | null;
}

export interface DeviceInfo {
  type: string;
  os: string;
}

// Asset object from backend - unified structure for all tracking assets
export interface Asset {
  type: 'img' | 'script' | 'iframe' | 'anchor';
  url: string;
  purpose: 'verification' | 'impression' | 'sync' | 'creative' | 'third_party' | 'click';
  vendor_hint: string | null;
}

export interface CreativeVariable {
  index: number;
  macro: string; // e.g., "CRV1", "CRV2"
  value: string;
}

export interface LocationVariable {
  index: number;
  macro: string; // e.g., "LV1", "LV2"
  value: string;
}

export interface ProcessedCampaignUrl {
  index: number;
  macro: string; // e.g., "URL0", "URL1"
  original: string; // Original URL with macros
  processed: string; // URL after macro replacement
  missing_variables?: string[]; // e.g., ["CRV3", "LV2"] - unresolved macros
}

export interface PreviewMetadata {
  creative_type: CreativeType;
  vendor?: string;
  size: string;
  macros_replaced: MacroReplacement[];
  // Legacy fields - kept for backward compatibility but deprecated
  // New backend returns unified 'assets' array instead
  pixels_found?: string[];
  click_trackers?: string[];
  // New unified assets array (primary source)
  assets?: Asset[];
  // Stripped assets when fire_tracking=false (live preview)
  stripped_assets?: Asset[];
  warnings: string[];
  settings_applied: {
    location: LocationInfo;
    device: DeviceInfo;
  };
  moment_type?: string;
  video_type?: string;
  // Live preview specific (optional)
  line_item_id?: string;
  cc_creative_id?: number;
  preview_source?: 'venice' | 'local';
  publishable_version?: number;
  last_published_version?: number;
  tracking_pixels_stripped?: boolean;
  stripped_verification_vendors?: string[];
  // Variable replacement details
  creative_variables?: CreativeVariable[];
  location_variables?: LocationVariable[];
  processed_campaign_urls?: ProcessedCampaignUrl[];
}

export interface PreviewRecord {
  id: number;
  public_route: string;
  campaign_id?: number; // Optional for Creative ID only scenario
  creative_id: number;
  location_id?: number;
  name?: string;
  preview_type?: string;
}

export interface LivePreviewDiagnostics {
  checks: {
    preview_compiled_identifiers_present: boolean;
    preview_compiled_identifiers_source?: string;
    line_item_id_present: boolean;
    line_item_in_venice: boolean;
    line_item_key_used?: string;
    bid_table_entry_present: boolean;
    venice_reachable: boolean;
    campaign_recently_published: boolean;
    tiles_published_campaign_version?: number | null;
    sync_status?: SyncStatus | null;
    dynamo_reachable?: boolean;
    dynamo?: DynamoData | null;
  };
  check_groups: CheckGroup[];
  failure_reason: string;
  failure_category: string;
  primary_message: string;
  suggested_actions: Array<{
    action: string;
    label: string;
    description: string;
    is_primary: boolean;
    why?: string;
  }>;
  user_facing: {
    title: string;
    status_level: 'action_required' | 'waiting' | 'blocked' | 'info';
    summary: string;
    next_steps: Array<{
      action: string;
      label: string;
      description: string;
      is_primary: boolean;
      why?: string;
    }>;
    glossary?: Array<{
      term: string;
      definition: string;
    }> | null;
  };
  technical_details: {
    diagnostics_id: string;
    generated_at: string;
    stage: string;
    venice_preview_endpoint: string | null;
    line_item_id: string | null;
    line_item_key_used: string | null;
    cc_creative_id: number | null;
    campaign_id: number | null;
    creative_id: number | null;
    campaign_publishable_version: number | null;
    campaign_last_published_version: number | null;
    tiles_published_campaign_version: number | null;
    sync_status: any;
    dynamo_version_used: number | null;
    dynamo_data_source: string | null;
    dynamo_pipeline: any;
    bid_response?: BidResponseDebugInfo | null;
    has_ad_markup?: boolean;
    preview_type?: string | null;
  };
}

export interface BidResponseDebugInfo {
  response_present: boolean;
  has_seatbid: boolean;
  has_bid?: boolean;
  bid_id?: string;
  bid_impid?: string;
  bid_price?: number;
  has_adm?: boolean;
  adm_length?: number;
  adm_preview?: string;
  nurl?: string;
  adomain?: string[];
  no_bid_reason?: number;
  no_bid_reason_description?: string;
  error?: string;
  raw_response: any;
}

export interface CheckGroup {
  title: string;
  items: Array<{
    key: string;
    label: string;
    status: boolean | 'pass' | 'fail' | 'pending' | 'unknown';
    raw?: string;
    detail?: string;
  }>;
}

export interface SyncStatus {
  status: string;
  details?: string;
  user_details?: string;
  time_remaining_seconds?: number;
  time_remaining_minutes?: number;
  data_source?: string;
  pending_stages?: string[];
  not_enqueued_stages?: string[];
  stages_syncing?: string[];
}

export interface DynamoData {
  data_source: string;
  versions_checked: number[];
  dynamo_items_considered_count: number;
  dynamo_item_version_used?: number;
  latest_version_found?: number;
  ventura_push_error: boolean;
  times?: {
    line_item_enqueue_time?: string;
    line_item_push_time?: string;
    bid_table_enqueue_time?: string;
    bid_table_push_time?: string;
    campaign_data_enqueue_time?: string;
    campaign_data_push_time?: string;
    tile_enqueue_time?: string;
    tile_push_time?: string;
    spark_tile_enqueue_time?: string;
    spark_tile_s3_upload_time?: string;
    spark_tile_push_time?: string;
  };
  pipeline?: {
    line_item?: string;
    bid_table?: string;
    campaign_data?: string;
    tile?: string;
    spark_tile?: string;
  };
}

export interface SimulatePreviewResponse {
  success: boolean;
  mode: PreviewMode;
  preview: PreviewRecord;
  preview_url: string;
  adm?: string;
  vast_url?: string;
  iframe_url?: string;
  metadata: PreviewMetadata;
  rtb?: {
    response: any;
  };
  errors: string[];
  diagnostics?: LivePreviewDiagnostics;
}

export interface SimulatePreviewRequest {
  campaign_id?: number; // Optional for Creative ID only scenario
  creative_id?: number; // Optional for raw tag mode
  location_id?: number;
  lat?: number; // Number, not string
  lon?: number; // Number, not string
  device_type?: DeviceType;
  os?: DeviceOS;
  tag_html?: string; // Raw tag HTML for raw tag mode
  campaign_urls?: string | string[]; // Optional: Override campaign click-through URLs
  pixel_urls?: string | string[]; // Optional: Override campaign pixel/tracking URLs
  fire_tracking?: boolean; // Controls whether tracking pixels fire
  save_qa_preview?: boolean; // Save preview for sharing
  selected_size?: string; // User-selected size for raw tag mode (e.g., "300x250")
  display_name?: string; // Optional custom name for shared preview
}

export interface ContextClient {
  id: number;
  name: string;
}

export interface ContextAdvertiser {
  id: number;
  name: string;
  client_id: number;
}

export interface ContextContract {
  id: number;
  name: string;
  advertiser_id: number;
}

export interface ContextInsertionOrder {
  id: number;
  name: string;
  contract_id: number;
}

export interface ContextCampaign {
  id: number;
  name: string;
  insertion_order_id: number;
  status: string;
  is_conquest?: boolean;
  is_moments?: boolean;
}

export interface ContextCreative {
  id: number;
  name: string;
  advertiser_id: number;
  ad_type: string;
  size: string;
  is_video: boolean;
  is_mraid: boolean;
  is_moments: boolean;
  // Optional field for hosted image creatives
  link?: string; // Hosted image URL (e.g., https://thinknear-hosted.thinknearhub.com/...)
}

export interface ContextLocation {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
}

export interface ContextDestination {
  latitude: number;
  longitude: number;
  name: string;
  address?: string;
}

export interface TargetedCreative {
  id: number;
  campaign_id: number;
  creative_id: number;
  creative: ContextCreative;
}

export interface PreviewContext {
  success: boolean;
  mode?: 'creative_only' | 'full_context';
  context: {
    // Full context fields (when mode === 'full_context')
    client?: ContextClient;
    advertiser?: ContextAdvertiser;
    contract?: ContextContract;
    insertion_order?: ContextInsertionOrder;
    campaign?: ContextCampaign;
    creative?: ContextCreative;
    locations?: ContextLocation[];
    conquest_destination?: ContextDestination;
    targeted_creatives?: TargetedCreative[];
    
    // Creative-only fields (when mode === 'creative_only')
    available_campaigns?: Array<{
      id: number;
      name: string;
      status: string;
      insertion_order_id: number;
    }>;
  };
}

export interface CreativeSize {
  width: number;
  height: number;
  label: string;
}

export const STANDARD_CREATIVE_SIZES: CreativeSize[] = [
  // Banner Sizes
  { width: 300, height: 250, label: '300x250 - Medium Rectangle' },
  { width: 728, height: 90, label: '728x90 - Leaderboard' },
  { width: 320, height: 50, label: '320x50 - Mobile Banner' },
  { width: 160, height: 600, label: '160x600 - Wide Skyscraper' },
  { width: 300, height: 600, label: '300x600 - Half Page' },
  { width: 970, height: 250, label: '970x250 - Billboard' },
  { width: 320, height: 480, label: '320x480 - Mobile Interstitial' },
  { width: 480, height: 320, label: '480x320 - Mobile Interstitial (Landscape)' },
  // Video Sizes (16:9 aspect ratio)
  { width: 320, height: 180, label: '320x180 - Mobile Video (16:9)' },
  { width: 480, height: 270, label: '480x270 - Mobile Video (16:9)' },
  { width: 640, height: 360, label: '640x360 - Standard Video (16:9)' },
  { width: 768, height: 432, label: '768x432 - Tablet Video (16:9)' },
  { width: 1024, height: 576, label: '1024x576 - Desktop Video (16:9)' },
  { width: 1280, height: 720, label: '1280x720 - HD Video (16:9)' },
  { width: 1920, height: 1080, label: '1920x1080 - Full HD Video (16:9)' },
];

export interface LivePreviewEligibility {
  eligible: boolean;
  campaign_id: number;
  creative_id: number;
  campaign: {
    id: number;
    name: string;
    is_paused: boolean;
    is_published: boolean;
    publishable_version: number;
    last_published_version: number;
    insertion_order_id: number;
    is_conquest: boolean;
    has_locations: boolean;
    has_campaign_urls: boolean;
  } | null;
  creative: {
    id: number;
    name: string;
    size: string;
    ad_type: string;
    is_video: boolean;
    is_mraid: boolean;
    has_tag: boolean;
  } | null;
  issues: Array<{
    category: string;
    severity: 'error' | 'warning';
    message: string;
    fix: string;
    current_value?: any;
    required_value?: any;
    // Publishing status - only present when a publish job is in progress
    publish_status?: {
      in_progress: boolean;
      status: 'running' | 'queued' | 'scheduled';
    };
    // Debug info for troubleshooting
    debug?: {
      malibu_query?: string;
      campaign_id?: number;
      sidekiq_status?: string;
      [key: string]: any;
    };
  }>;
  warnings: Array<{
    category: string;
    severity: 'warning' | 'info';
    message: string;
    current_value?: any;
    default_fallback?: any;
  }>;
  launch_steps: Array<{
    step: number;
    title: string;
    status: 'complete' | 'warning' | 'error';
    checks: Array<{
      name: string;
      status: 'pass' | 'fail' | 'warning';
      message: string;
    }>;
  }>;
  // Eligibility requirement sections
  requirements?: Array<{
    name: string;
    status: 'pass' | 'fail' | 'pending';
    details?: string;
  }>;
  // Compilation details when found
  compiled?: any;
}

// ---- Gallery Types ----

export type SiteContextType = 'blank' | 'forbes' | 'people' | 'espn' | 'bleacherreport' | 'allrecipes' | 'myrecipes' | 'instagram' | 'facebook' | 'wordswithfriends' | 'subwaysurfers';

export type SiteCategory = 'blank' | 'news' | 'sports' | 'recipe' | 'social' | 'game';

export const SITE_CONTEXT_CATEGORIES: Record<SiteCategory, { label: string; styles: Array<{ value: SiteContextType; label: string }> }> = {
  blank: { label: 'Blank', styles: [{ value: 'blank', label: 'No Site Context' }] },
  news: { label: 'News', styles: [{ value: 'forbes', label: 'Forbes' }, { value: 'people', label: 'People' }] },
  sports: { label: 'Sports', styles: [{ value: 'espn', label: 'ESPN' }, { value: 'bleacherreport', label: 'Bleacher Report' }] },
  recipe: { label: 'Recipe', styles: [{ value: 'allrecipes', label: 'AllRecipes' }, { value: 'myrecipes', label: 'MyRecipes' }] },
  social: { label: 'Social', styles: [{ value: 'instagram', label: 'Instagram' }, { value: 'facebook', label: 'Facebook' }] },
  game: { label: 'Game', styles: [{ value: 'wordswithfriends', label: 'Words With Friends' }, { value: 'subwaysurfers', label: 'Subway Surfers' }] },
};
export type Orientation = 'portrait' | 'landscape';
export type ChromeTheme = 'light' | 'dark';
export type GalleryViewMode = 'standard' | 'presentation';

export interface GalleryPreview {
  creative_id: number;
  preview_url: string | null;
  name: string;
  status: 'ready' | 'error';
  error?: string;
  width?: number;
  height?: number;
  is_mraid?: boolean;
  is_video?: boolean;
  adm?: string;
  screenshot_url?: string; // Pre-captured screenshot from Lambda
}

export interface GalleryConfig {
  device_type?: string;
  default_view_mode?: GalleryViewMode;
  default_site_context?: SiteContextType;
}

export interface GalleryResponse {
  gallery_id: number;
  public_route: string;
  share_url: string;
  name: string;
  campaign_id: number;
  creative_count: number;
  previews: GalleryPreview[];
  gallery_config: GalleryConfig;
  created_at: string;
  expires_at: string | null;
  view_count: number;
}

export interface CreateGalleryRequest {
  creative_ids: number[];
  campaign_id: number;
  name: string;
  device_type?: string;
  default_view_mode?: GalleryViewMode;
  default_site_context?: SiteContextType;
}

